// src/pages/scan/ScanResults.tsx
import React, { useState, useEffect } from 'react';
import ScanReportCTA from '../../components/scan/ScanReportCTA';
import ScanSummary from './ScanResults/ScanSummary';
import ScanDetections from './ScanResults/ScanDetections';
import ScanTableEmails from './ScanResults/ScanTableEmails';
import ScanHistory from '../../components/scan/ScanResults/ScanHistory/ScanHistory';
import ScanLeaksCards from '../../components/scan/ScanResults/ScanLeaksCards';
import ScanSimilarCompaniesDetails from './ScanResults/ScanSimilarCompaniesDetails';

interface ScanResultsProps {
  result: any;
  progress: {
    emails: number;
    vulnerabilities: number;
    ips: number;
    impersonation: number;
    leaks: number;
    global: number;
    similar_companies: number;
  };
  maskedEmails: string[];
  emailsCount: number;
  ips: number;
  impersonationCount: number;
  leaksCount: number;
  vulnerabilitiesCount: number;
  domain: string;
  t: (key: string) => string;
  onOpenReport: () => void;
  resetTable: boolean;
  scanHistory?: {
    domain?: string | null;
    lastScanDate?: string | null;
    scansLastYear?: number;
  } | null;
  blurredLeaks: any[];
  similarCompanies?: {
    organizationFound: boolean;
    totalSimilar: number;
    sameCity: number;
    sameCountry: number;
  };
}

const ScanResults: React.FC<ScanResultsProps> = ({
  scanHistory,
  result,
  progress,
  maskedEmails,
  emailsCount,
  ips,
  impersonationCount,
  leaksCount,
  vulnerabilitiesCount,
  domain,
  t,
  onOpenReport,
  resetTable,
  blurredLeaks,
  similarCompanies,
}) => {
  // Estado para el tiempo transcurrido desde que se montó el componente (en segundos)
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    // Al montar el componente, se inicia un contador que se actualiza cada segundo.
    const startTime = Date.now();
    const intervalId = setInterval(() => {
      const diffSeconds = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(diffSeconds);
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const showCTA =
    result?.status === 'completed' || elapsedTime >= 30;
  const variant =
    result?.status === 'completed'
      ? 'ready'
      : 'almost_ready';

  return (
    <div className="container mx-auto px-4 w-full">
      
      {showCTA && (
        <ScanReportCTA
          onClick={onOpenReport}
          variant={variant}
        />
      )}

      {scanHistory && (
        <ScanHistory
          t={t}
          scanHistory={{
            domain: scanHistory.domain ?? undefined,
            lastScanDate: scanHistory.lastScanDate ?? undefined,
            scansLastYear: scanHistory.scansLastYear,
          }}
        />
      )}

      <div className="bg-gray-800 rounded-xl p-8 shadow-xl w-full">
        {/* 1) Resumen global (score, estado, etc.) */}
        <ScanSummary result={result} t={t} />

        {/* 2) Detecciones (barras de progreso, contadores, etc.) */}
        <ScanDetections
          progress={progress}
          isLoading={result?.status !== 'completed'}
          t={t}
          emailsCount={emailsCount}
          ipsCount={ips}
          impersonationCount={impersonationCount}
          leaksCount={leaksCount}
          vulnerabilitiesCount={vulnerabilitiesCount}
          similarCompaniesCount={similarCompanies?.totalSimilar ?? 0}
        />

        <ScanLeaksCards
          blurredLeaks={blurredLeaks}
          totalCount={leaksCount}
          t={t}
          onViewMore={onOpenReport}
        />

        <ScanTableEmails
          t={t}
          reset={resetTable}
          emailsCount={emailsCount}
          maskedEmails={maskedEmails}
          onViewMore={onOpenReport}
        />

        <ScanSimilarCompaniesDetails
          t={t}
          organizationFound={similarCompanies?.organizationFound ?? false}
          totalSimilar={similarCompanies?.totalSimilar ?? 0}
          sameCity={similarCompanies?.sameCity ?? 0}
          sameCountry={similarCompanies?.sameCountry ?? 0}
          onViewMore={onOpenReport}
        />
      </div>
    </div>
  );
};

export default ScanResults;
